import React, { useMemo } from 'react';
import {
  CalculatorOutlined,
  DollarOutlined,
  FileTextOutlined,
  RiseOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Col, Row } from 'antd';
import PropTypes from 'prop-types';

import { currencyFormatter, percentFormatter } from '../../../../helpers/inputParsers';

const getSummaryCards = ({
  totalBilled,
  actualSpend,
  overBudget,
  contractValue,
  totalEstimated,
  laborCost,
  allocatedHours,
  percentInvoiced,
  profitMargin,
}) => ([
  {
    key: 'totalBilled',
    label: 'Total Billed Amount',
    value: totalBilled,
    sub: `${percentInvoiced} invoiced`,
    subGreen: true,
    icon: DollarOutlined,
    iconClass: 'icon-blue',
  },
  {
    key: 'actualSpend',
    label: 'Actual Spend',
    value: actualSpend,
    icon: RiseOutlined,
    iconClass: 'icon-orange',
  },
  {
    key: 'overBudget',
    label: 'Over Budget Items',
    value: overBudget,
    valueRed: true,
    icon: WarningOutlined,
    iconClass: 'icon-red',
  },
  {
    key: 'contractValue',
    label: 'Current Contract Value',
    value: contractValue,
    sub: 'Base + change orders',
    icon: FileTextOutlined,
    iconClass: 'icon-green',
  },
  {
    key: 'totalEstimated',
    label: 'Total Estimated Amount',
    value: totalEstimated,
    sub: `${profitMargin}% profit margin`,
    subGreen: true,
    icon: CalculatorOutlined,
    iconClass: 'icon-purple',
  },
  {
    key: 'laborCost',
    label: 'Estimated Labor Cost',
    value: laborCost,
    sub: `${allocatedHours} hours allocated`,
    icon: TeamOutlined,
    iconClass: 'icon-blue',
  },
]);

export default function CostTrackingSummaryOverview({
  summaryData,
  sovSummaryValues,
}) {
  const {
    expectedProfit,
    profitMargin,
  } = useMemo(() => {
    const { totalContractValue } = sovSummaryValues;
    const [{ estimatedTotalCost } = {}] = summaryData ?? [];
    const profit = (totalContractValue ?? 0) - (estimatedTotalCost ?? 0);
    const margin = totalContractValue ? (profit / totalContractValue) * 100 : 0;
    return {
      expectedProfit: profit,
      profitMargin: margin.toFixed(2),
    };
  }, [summaryData, sovSummaryValues]);

  const summaryCards = useMemo(() => {
    const {
      totalBilledToDate,
      totalContractValue,
    } = sovSummaryValues;
    const [{
      estimatedTotalCost,
      estimatedLaborCost,
      actualTotalSpend,
      overBudgetItems,
      allocatedHours,
    } = {}] = summaryData ?? [];
    const percentInvoiced = totalContractValue ? (totalBilledToDate / totalContractValue) * 100 : 0;
    return getSummaryCards({
      totalBilled: currencyFormatter(totalBilledToDate, false),
      actualSpend: currencyFormatter(actualTotalSpend, false),
      overBudget: overBudgetItems,
      contractValue: currencyFormatter(totalContractValue, false),
      totalEstimated: currencyFormatter(estimatedTotalCost, false),
      laborCost: currencyFormatter(estimatedLaborCost, false),
      allocatedHours,
      percentInvoiced: percentFormatter(percentInvoiced.toFixed(2), false),
      profitMargin,
    });
  }, [summaryData, sovSummaryValues, profitMargin]);

  return (
    <div
      className="budget-tracking-panel budget-tracking-border"
      id="overview"
    >
      <div className="budget-tracking-panel-content">
        <div className="cost-tracking-summary-header">
          <h2 className="cost-tracking-summary-title">Summary Overview</h2>
          <span className="cost-tracking-summary-updated">Last updated: 12/25/2025</span>
        </div>
        <Row gutter={[16, 16]}>
          {summaryCards.map((card) => {
            const IconComponent = card.icon;
            return (
              <Col xs={24} sm={12} lg={8} key={card.key}>
                <div className="cost-tracking-summary-card">
                  <div className={`cost-tracking-summary-card-icon ${card.iconClass}`}>
                    <IconComponent style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <div className="cost-tracking-summary-card-label">{card.label}</div>
                    <div className={`cost-tracking-summary-card-value ${card.valueRed ? 'value-red' : ''}`}>
                      {card.value}
                    </div>
                    <div className={`cost-tracking-summary-card-sub ${card.subGreen ? 'sub-green' : ''}`}>
                      {card.sub}
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
        <div className="cost-tracking-summary-profit">
          <h3 className="cost-tracking-summary-profit-title">Profit Analysis</h3>
          <div className="cost-tracking-summary-profit-row">
            <div className="cost-tracking-summary-profit-item">
              <div className="cost-tracking-summary-profit-label">Expected Profit</div>
              <div className="cost-tracking-summary-profit-value">{currencyFormatter(expectedProfit, false)}</div>
            </div>
            <div className="cost-tracking-summary-profit-item" style={{ marginLeft: '35%' }}>
              <div className="cost-tracking-summary-profit-label">Profit Margin</div>
              <div className="cost-tracking-summary-profit-value">{percentFormatter(profitMargin, false)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
CostTrackingSummaryOverview.propTypes = {
  summaryData: PropTypes.arrayOf(PropTypes.shape({
    estimatedTotalCost: PropTypes.number,
    estimatedLaborCost: PropTypes.number,
    overBudgetItems: PropTypes.number,
    actualTotalSpend: PropTypes.number,
  })),
  sovSummaryValues: PropTypes.object,
};

CostTrackingSummaryOverview.defaultProps = {
  summaryData: [],
  sovSummaryValues: {},
};
